import { type DealerApplication, type DealerApplicationState } from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import {
	approveDealerApplication,
	fetchDealerApplications,
	rejectDealerApplication,
} from './api';
import { KybDocuments } from './kyb-documents';

type FilterState = DealerApplicationState | 'all';

/** Tab order, as the artboard lays it out left to right. */
const FILTERS: FilterState[] = ['pending', 'approved', 'rejected', 'all'];

/** The minimum rejection-reason length the API enforces. */
const MIN_REASON_LENGTH = 10;

/**
 * Status vocabulary — the same three-way decision the KYC queue makes, so it
 * wears the same colours: the amber accent for "waiting on you", green for
 * approved, red for rejected. The tint appears twice per card (the chip, and
 * the 3px rule across the card's top edge) and is therefore stated once.
 */
const TONE: Record<DealerApplicationState, { chip: string; top: string }> = {
	pending: {
		chip: 'bg-cf-warn-tint text-cf-warn',
		top: 'shadow-[inset_0_3px_0_var(--color-cf-accent)]',
	},
	approved: {
		chip: 'bg-cf-success-tint text-cf-success',
		top: 'shadow-[inset_0_3px_0_var(--color-cf-success)]',
	},
	rejected: {
		chip: 'bg-cf-danger-tint text-cf-danger',
		top: 'shadow-[inset_0_3px_0_var(--color-cf-danger)]',
	},
};

/**
 * Dealer (KYB) applications — `Redesign-D-Admin{,-Mobile}`, screen «Дилери».
 *
 * A card per application rather than a row, because approving a dealer is not a
 * glance decision: the company, who is behind it, what documents arrived and
 * what they asked for all have to be on screen at once. Desktop flows those
 * cards into a `minmax(360px, 1fr)` grid; mobile stacks them.
 *
 * Like the KYC queue this screen is usually empty, so the artboard draws that
 * state explicitly — «Заявок у цьому статусі немає».
 */
export function DealerReviewPage() {
	const queryClient = useQueryClient();
	const [filter, setFilter] = useState<FilterState>('pending');
	const [expandedDocs, setExpandedDocs] = useState<string | null>(null);
	const [rejectTarget, setRejectTarget] = useState<DealerApplication | null>(null);
	const [rejectReason, setRejectReason] = useState('');

	const applicationsQuery = useQuery({
		queryKey: ['dealer-applications'],
		// Every state in one read: the tab counts are only honest if the page
		// holds the whole list (see fetchDealerApplications).
		queryFn: () => fetchDealerApplications(),
	});

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ['dealer-applications'] });

	const approveMutation = useMutation({
		mutationFn: (id: string) => approveDealerApplication(id),
		onSuccess: () => {
			toast.success(t('dealer.review.approveSuccess'));
			void invalidate();
		},
		onError: () => toast.error(t('dealer.review.actionError')),
	});

	const rejectMutation = useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) =>
			rejectDealerApplication(id, reason),
		onSuccess: () => {
			toast.success(t('dealer.review.rejectSuccess'));
			setRejectTarget(null);
			setRejectReason('');
			void invalidate();
		},
		onError: () => toast.error(t('dealer.review.actionError')),
	});

	const error = applicationsQuery.error;
	if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
		return <AccessDenied />;
	}

	const applications = applicationsQuery.data?.applications ?? [];
	const stateOf = (application: DealerApplication): DealerApplicationState =>
		application.state ?? 'pending';
	const countOf = (key: FilterState) =>
		key === 'all'
			? applications.length
			: applications.filter((application) => stateOf(application) === key).length;

	const filtered = applications.filter(
		(application) => filter === 'all' || stateOf(application) === filter,
	);

	const applicationId = (application: DealerApplication): string =>
		String((application as { _id?: unknown })._id ?? '');

	const isBusy = approveMutation.isPending || rejectMutation.isPending;

	return (
		<>
			{/* ── Header ──────────────────────────────────────────────────────── */}
			<div>
				<h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
					{t('dealer.review.title')}
				</h1>
				<p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
					<span className="lg:hidden">{t('dealer.review.subtitleShort')}</span>
					<span className="hidden lg:inline">{t('dealer.review.subtitle')}</span>
				</p>
			</div>

			{/* ── Filter tabs ─────────────────────────────────────────────────── */}
			<div className="-mx-4 flex gap-1.5 overflow-x-auto px-4 lg:hidden">
				{FILTERS.map((value) => (
					<button
						key={value}
						type="button"
						onClick={() => setFilter(value)}
						className={`h-9 shrink-0 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap ${
							filter === value
								? 'border-cf-brand bg-cf-brand text-white'
								: 'border-cf-line bg-cf-surface text-cf-ink'
						}`}
					>
						{t(`dealer.review.filters.${value}`)} · {countOf(value)}
					</button>
				))}
			</div>
			<div role="tablist" className="hidden w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px] lg:flex">
				{FILTERS.map((value) => (
					<button
						key={value}
						type="button"
						role="tab"
						aria-selected={filter === value}
						onClick={() => setFilter(value)}
						className={`h-9 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap ${
							filter === value ? 'bg-cf-brand text-white' : 'bg-transparent text-cf-ink'
						}`}
					>
						{t(`dealer.review.filters.${value}`)}{' '}
						<span className="font-mono text-[11px] font-medium opacity-70">
							{countOf(value)}
						</span>
					</button>
				))}
			</div>

			{/* ── Applications ────────────────────────────────────────────────── */}
			{applicationsQuery.isPending ? (
				<Notice text={t('dealer.review.loading')} />
			) : applicationsQuery.isError ? (
				<Notice text={t('dealer.review.failed')} />
			) : filtered.length === 0 ? (
				<EmptyState />
			) : (
				<div className="grid gap-3 lg:grid-cols-[repeat(auto-fill,minmax(360px,1fr))]">
					{filtered.map((application) => {
						const id = applicationId(application);
						return (
							<ApplicationCard
								key={id}
								application={application}
								state={stateOf(application)}
								docsOpen={expandedDocs === id}
								onToggleDocs={() => setExpandedDocs(expandedDocs === id ? null : id)}
								onApprove={() => approveMutation.mutate(id)}
								onReject={() => {
									setRejectTarget(application);
									setRejectReason('');
								}}
								isBusy={isBusy}
							/>
						);
					})}
				</div>
			)}

			{/* ── Reject dialog ───────────────────────────────────────────────── */}
			{rejectTarget && (
				<div
					role="dialog"
					aria-modal="true"
					onClick={() => setRejectTarget(null)}
					className="fixed inset-0 z-50 flex items-center justify-center bg-cf-ink/50 p-4"
				>
					<div
						onClick={(event) => event.stopPropagation()}
						className="w-full max-w-md rounded-cf-lg border border-cf-line bg-cf-surface p-6"
					>
						<h3 className="text-[17px] font-semibold tracking-[-0.02em]">
							{t('dealer.review.rejectModal.title')}
						</h3>
						<p className="mt-2 text-[13px] leading-[1.5] text-cf-ink-subtle">
							{t('dealer.review.rejectModal.body')}
						</p>
						<textarea
							value={rejectReason}
							rows={4}
							onChange={(event) => setRejectReason(event.target.value)}
							placeholder={t('dealer.review.rejectModal.placeholder')}
							className="mt-3 w-full resize-y rounded-cf-md border border-cf-line px-3 py-2.5 text-[13px]"
						/>
						<p className="mt-1 text-right font-mono text-[11px] text-cf-ink-subtle">
							{rejectReason.trim().length}/{MIN_REASON_LENGTH}
						</p>
						<div className="mt-4 flex justify-end gap-2">
							<button
								type="button"
								onClick={() => setRejectTarget(null)}
								className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink"
							>
								{t('dealer.review.rejectModal.cancel')}
							</button>
							<button
								type="button"
								disabled={rejectReason.trim().length < MIN_REASON_LENGTH || isBusy}
								onClick={() =>
									rejectMutation.mutate({
										id: applicationId(rejectTarget),
										reason: rejectReason.trim(),
									})
								}
								className="h-10 rounded-cf-md bg-cf-danger px-3.5 text-sm font-semibold text-white disabled:opacity-50"
							>
								{rejectMutation.isPending
									? t('dealer.review.rejecting')
									: t('dealer.review.rejectModal.confirm')}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

/**
 * One application.
 *
 * The artboard's fact rows are KYB verdicts a human recorded («Витяг з ЄДР ·
 * 09.09», «Документ директора · очікує»). The API stores no such verdicts, so
 * these rows carry what the record does hold — tax id, legal form, contact,
 * submission date — in the same key/value shape, with the real uploaded
 * documents one tap below.
 */
function ApplicationCard({
	application,
	state,
	docsOpen,
	onToggleDocs,
	onApprove,
	onReject,
	isBusy,
}: {
	application: DealerApplication;
	state: DealerApplicationState;
	docsOpen: boolean;
	onToggleDocs: () => void;
	onApprove: () => void;
	onReject: () => void;
	isBusy: boolean;
}) {
	const tone = TONE[state] ?? TONE.pending;
	const submitted = application.createdAt
		? new Date(application.createdAt).toLocaleDateString('uk-UA')
		: '—';

	const facts: Array<{ key: string; value: string; tone: string }> = [
		{
			key: t('dealer.review.taxId'),
			value: application.taxId,
			tone: 'text-cf-ink',
		},
		{
			key: t('dealer.review.legalFormLabel'),
			value: t(`dealer.legalForm.${application.legalForm}`),
			tone: 'text-cf-ink',
		},
		{
			key: t('dealer.review.contact'),
			value: application.contactEmail,
			tone: 'text-cf-ink',
		},
		{ key: t('dealer.review.submitted'), value: submitted, tone: 'text-cf-ink-subtle' },
	];

	return (
		<section
			className={`flex flex-col gap-2.5 rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-3.5 lg:gap-3 lg:px-5 lg:py-[18px] ${tone.top}`}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
						{application.companyName}
					</h2>
					<p className="mt-[3px] text-xs text-cf-ink-subtle lg:mt-1 lg:text-[13px]">
						{application.contactName} · {application.contactPhone}
					</p>
				</div>
				<span
					className={`inline-flex shrink-0 rounded-cf-sm px-[7px] py-[3px] font-mono text-[9px] tracking-[0.08em] whitespace-nowrap uppercase lg:px-2 lg:py-1 lg:text-[10px] ${tone.chip}`}
				>
					<span className="lg:hidden">{t(`dealer.status.${state}`)}</span>
					<span className="hidden lg:inline">{t(`dealer.review.stateLong.${state}`)}</span>
				</span>
			</div>

			<div className="flex flex-col">
				{facts.map((fact) => (
					<div
						key={fact.key}
						className="flex justify-between gap-2 border-b border-cf-line-soft py-1.5 text-[13px] lg:py-[7px]"
					>
						<span className="text-cf-ink-muted">{fact.key}</span>
						<span className={`min-w-0 truncate font-mono text-[11px] ${fact.tone}`}>
							{fact.value}
						</span>
					</div>
				))}
			</div>

			{/* KYB scans — fetched on demand, so a queue of 20 does not fire 20 reads. */}
			<div>
				<button
					type="button"
					onClick={onToggleDocs}
					aria-expanded={docsOpen}
					className="inline-flex items-center gap-1 text-[13px] font-semibold text-cf-brand"
				>
					{docsOpen ? (
						<ChevronUp size={14} strokeWidth={1.75} />
					) : (
						<ChevronDown size={14} strokeWidth={1.75} />
					)}
					{docsOpen ? t('dealer.review.hideDocs') : t('dealer.review.viewDocs')}
				</button>
				{docsOpen && (
					<div className="mt-2">
						<KybDocuments userId={application.userId} />
					</div>
				)}
			</div>

			{(application.message || (state === 'rejected' && application.reviewReason)) && (
				<p
					className={`text-xs leading-[1.4] ${
						state === 'rejected' && application.reviewReason
							? 'text-cf-danger'
							: 'text-cf-ink-subtle'
					}`}
				>
					{state === 'rejected' && application.reviewReason
						? application.reviewReason
						: application.message}
				</p>
			)}

			{state === 'pending' && (
				<div className="flex gap-2">
					<button
						type="button"
						onClick={onApprove}
						disabled={isBusy}
						className="h-11 flex-1 rounded-cf-md bg-cf-success text-[13px] font-semibold text-white disabled:opacity-50 lg:h-10"
					>
						{t('dealer.review.approve')}
					</button>
					<button
						type="button"
						onClick={onReject}
						disabled={isBusy}
						className="h-11 flex-1 rounded-cf-md border border-[rgba(220,38,38,0.35)] bg-cf-surface text-[13px] font-semibold text-cf-danger disabled:opacity-50 lg:h-10"
					>
						{t('dealer.review.reject')}
					</button>
				</div>
			)}
		</section>
	);
}

/**
 * «Заявок у цьому статусі немає», over the illustration the artboard pairs it
 * with (90px on the phone, 110px on the desktop).
 */
function EmptyState() {
	return (
		<section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center lg:px-6 lg:py-10">
			<img
				src="/assets/cf/illu/account.png"
				alt=""
				className="mx-auto mb-2.5 block h-[90px] w-auto lg:mb-3 lg:h-[110px]"
			/>
			<p className="text-base font-semibold lg:text-[18px]">{t('dealer.review.empty')}</p>
		</section>
	);
}

function Notice({ text }: { text: string }) {
	return (
		<section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-7 text-center text-[13px] text-cf-ink-subtle lg:px-6 lg:py-10">
			{text}
		</section>
	);
}
